/* eslint-disable no-cond-assign, no-continue, no-use-before-define */
/**
 * Shared portal document tree: parse once, mutate in place, serialize on action.
 *
 * Node kinds:
 * - block   — hyphenated portal component (hero, content-heading, …)
 * - columns — <row gap> with column children
 * - column  — <column span> (only as child of columns)
 * - section — <portal-section>
 * - tabs    — <portal-tabs>
 * - tab     — <portal-tab name>
 * - wrapper — unknown HTML element that contains portal structure
 * - html    — leftover markup / text
 */

const STRUCTURAL_TAGS = new Set([
  'row',
  'column',
  'portal-section',
  'portal-tabs',
  'portal-tab',
]);

const LAYOUT_INSERT_TAGS = new Set([
  'layout-columns',
  'portal-section',
  'portal-tabs',
]);

const DEFAULT_CONTENT = {
  'content-heading': 'Your heading here',
  'content-text': 'Write your paragraph text here...',
  'content-button': 'Click here',
};

// Custom elements: hyphenated tags, or single-word tags that aren't HTML
const COMPONENT_TAG_RE = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;
const voidTags = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);
const HTML_ELEMENT_TAGS = new Set([
  'a',
  'abbr',
  'address',
  'article',
  'aside',
  'audio',
  'b',
  'blockquote',
  'body',
  'button',
  'canvas',
  'caption',
  'cite',
  'code',
  'colgroup',
  'data',
  'datalist',
  'dd',
  'del',
  'details',
  'dfn',
  'dialog',
  'div',
  'dl',
  'dt',
  'em',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'head',
  'header',
  'hgroup',
  'html',
  'i',
  'iframe',
  'ins',
  'kbd',
  'label',
  'legend',
  'li',
  'main',
  'map',
  'mark',
  'menu',
  'meter',
  'nav',
  'noscript',
  'object',
  'ol',
  'optgroup',
  'option',
  'output',
  'p',
  'picture',
  'pre',
  'progress',
  'q',
  'rp',
  'rt',
  'ruby',
  's',
  'samp',
  'script',
  'section',
  'select',
  'slot',
  'small',
  'span',
  'strong',
  'style',
  'sub',
  'summary',
  'sup',
  'svg',
  'table',
  'tbody',
  'td',
  'template',
  'textarea',
  'tfoot',
  'th',
  'thead',
  'time',
  'title',
  'tr',
  'u',
  'ul',
  'var',
  'video',
  ...voidTags,
]);

const isPortalComponentTag = tagName =>
  COMPONENT_TAG_RE.test(tagName) &&
  !STRUCTURAL_TAGS.has(tagName) &&
  !HTML_ELEMENT_TAGS.has(tagName);

const OPEN_TAG_RE =
  /<!--[\s\S]*?-->|<([a-zA-Z][a-zA-Z0-9-]*)((?:\s+[a-zA-Z_:][-a-zA-Z0-9_:.]*="[^"]*")*)\s*(\/?)>/g;

let idCounter = 0;
const nextId = () => {
  idCounter += 1;
  return `n-${idCounter}-${Math.random().toString(36).slice(2, 8)}`;
};

const parseAttrs = attrString => {
  const props = {};
  if (!attrString) return props;
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)="([^"]*)"/g;
  let m;
  while ((m = re.exec(attrString)) !== null) {
    props[m[1]] = m[2];
  }
  return props;
};

const attrsToString = props => {
  const entries = Object.entries(props || {}).filter(
    ([, v]) => v != null && v !== ''
  );
  if (!entries.length) return '';
  return ` ${entries.map(([k, v]) => `${k}="${v}"`).join(' ')}`;
};

const findClosingTag = (str, tagName, fromIndex) => {
  const openRe = new RegExp(`<${tagName}(?:\\s[^>]*)?>`, 'gi');
  const closeRe = new RegExp(`</${tagName}>`, 'gi');
  openRe.lastIndex = fromIndex;
  closeRe.lastIndex = fromIndex;

  let depth = 1;
  let pos = fromIndex;
  while (depth > 0 && pos < str.length) {
    openRe.lastIndex = pos;
    closeRe.lastIndex = pos;
    const openMatch = openRe.exec(str);
    const closeMatch = closeRe.exec(str);

    if (!closeMatch) return -1;

    if (openMatch && openMatch.index < closeMatch.index) {
      depth += 1;
      pos = openMatch.index + openMatch[0].length;
    } else {
      depth -= 1;
      if (depth === 0) return closeMatch.index;
      pos = closeMatch.index + closeMatch[0].length;
    }
  }
  return -1;
};

const makeNode = partial => ({
  id: nextId(),
  children: [],
  props: {},
  content: '',
  ...partial,
});

const parseChildren = str => {
  if (!str || !str.trim()) return [];

  const nodes = [];
  let lastIndex = 0;
  const re = new RegExp(OPEN_TAG_RE.source, 'g');
  let match;

  while ((match = re.exec(str)) !== null) {
    // HTML comment
    if (match[0].startsWith('<!--')) {
      const before = str.slice(lastIndex, match.index);
      if (before.trim()) {
        nodes.push(makeNode({ kind: 'html', content: before.trim() }));
      }
      nodes.push(makeNode({ kind: 'html', content: match[0] }));
      lastIndex = match.index + match[0].length;
      continue;
    }

    const tagName = match[1].toLowerCase();
    const attrString = match[2] || '';
    const selfClosing = match[3] === '/' || voidTags.has(tagName);
    const openEnd = match.index + match[0].length;

    const before = str.slice(lastIndex, match.index);
    if (before.trim()) {
      nodes.push(makeNode({ kind: 'html', content: before.trim() }));
    }

    if (selfClosing) {
      if (isPortalComponentTag(tagName)) {
        nodes.push(
          makeNode({
            kind: 'block',
            tagName,
            props: parseAttrs(attrString),
            content: '',
          })
        );
      } else {
        nodes.push(makeNode({ kind: 'html', content: match[0] }));
      }
      lastIndex = openEnd;
      re.lastIndex = openEnd;
      continue;
    }

    const closeIndex = findClosingTag(str, tagName, openEnd);
    if (closeIndex === -1) {
      // Unclosed tag — treat remainder as html
      nodes.push(
        makeNode({ kind: 'html', content: str.slice(match.index).trim() })
      );
      lastIndex = str.length;
      break;
    }

    const inner = str.slice(openEnd, closeIndex);
    const afterClose = closeIndex + tagName.length + 3; // </tagName>
    const props = parseAttrs(attrString);

    if (tagName === 'row') {
      const cols = parseColumns(inner);
      const gap = props.gap != null ? Number(props.gap) : 6;
      const rowProps = { ...props };
      delete rowProps.gap;
      delete rowProps['data-portal-id'];
      nodes.push(
        makeNode({
          kind: 'columns',
          gap: Number.isNaN(gap) ? 6 : Math.min(12, Math.max(0, gap)),
          props: rowProps,
          children: cols,
        })
      );
    } else if (tagName === 'portal-section') {
      const sectionProps = { ...props };
      delete sectionProps['data-portal-id'];
      nodes.push(
        makeNode({
          kind: 'section',
          tagName: 'portal-section',
          props: sectionProps,
          children: parseChildren(inner),
        })
      );
    } else if (tagName === 'portal-tabs') {
      nodes.push(
        makeNode({
          kind: 'tabs',
          tagName: 'portal-tabs',
          props,
          children: parseChildren(inner).filter(n => n.kind === 'tab'),
        })
      );
    } else if (tagName === 'portal-tab') {
      nodes.push(
        makeNode({
          kind: 'tab',
          tagName: 'portal-tab',
          props,
          children: parseChildren(inner),
        })
      );
    } else if (isPortalComponentTag(tagName)) {
      // Leaf component — keep inner as content if it has no nested structure
      const nested = parseChildren(inner);
      if (
        nested.length === 0 ||
        (nested.length === 1 && nested[0].kind === 'html')
      ) {
        nodes.push(
          makeNode({
            kind: 'block',
            tagName,
            props,
            content: inner.trim(),
          })
        );
      } else {
        // Component with nested portal nodes — treat as wrapper-like block with children
        nodes.push(
          makeNode({
            kind: 'block',
            tagName,
            props,
            content: '',
            children: nested,
          })
        );
      }
    } else if (tagName === 'column') {
      // Orphan column outside a row — wrap as single-column row
      const span = props.span != null ? Number(props.span) : 12;
      nodes.push(
        makeNode({
          kind: 'columns',
          gap: 6,
          children: [
            makeNode({
              kind: 'column',
              span: Number.isNaN(span) ? 12 : Math.min(12, Math.max(1, span)),
              children: parseChildren(inner),
            }),
          ],
        })
      );
    } else {
      // Unknown HTML element — if it contains portal structure, keep as wrapper
      const nested = parseChildren(inner);
      const hasStructure = nested.some(
        n => n.kind !== 'html' || (n.children && n.children.length)
      );
      if (hasStructure && nested.some(n => n.kind !== 'html')) {
        nodes.push(
          makeNode({
            kind: 'wrapper',
            tagName,
            props,
            children: nested,
          })
        );
      } else {
        nodes.push(
          makeNode({
            kind: 'html',
            content: str.slice(match.index, afterClose).trim(),
          })
        );
      }
    }

    lastIndex = afterClose;
    re.lastIndex = afterClose;
  }

  const trailing = str.slice(lastIndex).trim();
  if (trailing) {
    nodes.push(makeNode({ kind: 'html', content: trailing }));
  }

  return nodes;
};

const parseColumns = inner => {
  const columns = [];
  const re = new RegExp(OPEN_TAG_RE.source, 'g');
  let match;
  let lastIndex = 0;
  const leftovers = [];

  while ((match = re.exec(inner)) !== null) {
    if (match[0].startsWith('<!--')) continue;
    const tagName = match[1].toLowerCase();
    if (tagName !== 'column') continue;

    const before = inner.slice(lastIndex, match.index).trim();
    if (before) leftovers.push(before);

    const attrString = match[2] || '';
    const selfClosing = match[3] === '/';
    const openEnd = match.index + match[0].length;
    const props = parseAttrs(attrString);
    const spanRaw = props.span != null ? Number(props.span) : null;
    const span =
      spanRaw == null || Number.isNaN(spanRaw)
        ? null
        : Math.min(12, Math.max(1, spanRaw));

    if (selfClosing) {
      columns.push(makeNode({ kind: 'column', span, children: [] }));
      lastIndex = openEnd;
      re.lastIndex = openEnd;
      continue;
    }

    const closeIndex = findClosingTag(inner, 'column', openEnd);
    if (closeIndex === -1) break;
    const colInner = inner.slice(openEnd, closeIndex);
    columns.push(
      makeNode({
        kind: 'column',
        span,
        children: parseChildren(colInner),
      })
    );
    lastIndex = closeIndex + '</column>'.length;
    re.lastIndex = lastIndex;
  }

  const trailing = inner.slice(lastIndex).trim();
  if (trailing) leftovers.push(trailing);

  if (!columns.length) {
    return [
      makeNode({
        kind: 'column',
        span: 12,
        children: parseChildren(inner),
      }),
    ];
  }

  // Leftover content outside columns goes into first column
  if (leftovers.length) {
    const extra = leftovers.join('\n');
    columns[0].children = [...parseChildren(extra), ...columns[0].children];
  }

  return columns;
};

export function parse(doc) {
  idCounter = 0;
  return parseChildren(doc || '');
}

/** Event lifecycle stages that can be assigned to portal pages. */
export const SYSTEM_STAGES = [
  'draft',
  'inquiry',
  'confirmed',
  'completed',
  'lost',
  'archived',
];

const DEFAULT_PAGE_ID = 'pg-default';

const nextPageId = stage => {
  if (!stage) return DEFAULT_PAGE_ID;
  return `pg-${stage}-${Math.random().toString(36).slice(2, 6)}`;
};

const PAGE_OPEN_RE =
  /<portal-page((?:\s+[a-zA-Z_:][-a-zA-Z0-9_:.]*="[^"]*")*)\s*>/gi;

/**
 * Split a stored document into lifecycle pages.
 * No <portal-page> wrappers → single Default page with the whole document.
 */
export function parsePages(doc) {
  idCounter = 0;
  const source = doc || '';
  const pages = [];
  const re = new RegExp(PAGE_OPEN_RE.source, 'gi');
  let match;
  let found = false;

  while ((match = re.exec(source)) !== null) {
    found = true;
    const attrString = match[1] || '';
    const openEnd = match.index + match[0].length;
    const closeIndex = findClosingTag(source, 'portal-page', openEnd);
    if (closeIndex === -1) break;

    const props = parseAttrs(attrString);
    const stageRaw = (props['system-stage'] || props.system_stage || '')
      .toString()
      .trim();
    const systemStage = SYSTEM_STAGES.includes(stageRaw) ? stageRaw : null;
    const id =
      (props.id || '').toString().trim() ||
      (systemStage ? `pg-${systemStage}` : DEFAULT_PAGE_ID);
    const inner = source.slice(openEnd, closeIndex);
    pages.push({
      id,
      systemStage,
      nodes: parseChildren(inner),
    });
    re.lastIndex = closeIndex + '</portal-page>'.length;
  }

  if (!found) {
    return [
      {
        id: DEFAULT_PAGE_ID,
        systemStage: null,
        nodes: parseChildren(source),
      },
    ];
  }

  // Ensure exactly one Default (unassigned) page — first without a stage, or prepend empty.
  if (!pages.some(p => !p.systemStage)) {
    pages.unshift({
      id: DEFAULT_PAGE_ID,
      systemStage: null,
      nodes: [],
    });
  }

  // Dedupe stages: keep first occurrence of each systemStage
  const seen = new Set();
  return pages.filter(page => {
    if (!page.systemStage) {
      if (seen.has('__default__')) return false;
      seen.add('__default__');
      return true;
    }
    if (seen.has(page.systemStage)) return false;
    seen.add(page.systemStage);
    return true;
  });
}

/**
 * Persist pages. Single Default page → plain markup (backward compatible).
 * Two or more pages → wrap each in <portal-page>.
 */
export function serializePages(pages) {
  const list = pages || [];
  if (!list.length) return '';

  if (list.length === 1 && !list[0].systemStage) {
    return serialize(list[0].nodes);
  }

  return list
    .map(page => {
      const id = page.id || nextPageId(page.systemStage);
      const stageAttr = page.systemStage
        ? ` system-stage="${page.systemStage}"`
        : '';
      const body = serialize(page.nodes || []);
      const indentedBody = body
        ? body
            .split('\n')
            .map(line => `  ${line}`)
            .join('\n')
        : '';
      return indentedBody
        ? `<portal-page id="${id}"${stageAttr}>\n${indentedBody}\n</portal-page>`
        : `<portal-page id="${id}"${stageAttr}></portal-page>`;
    })
    .join('\n');
}

/** Inner document body for a stage (fallback: Default / first page). */
export function documentBodyForStage(doc, stage) {
  const pages = parsePages(doc);
  const match = stage ? pages.find(p => p.systemStage === stage) : null;
  const page = match || pages.find(p => !p.systemStage) || pages[0];
  return page ? serialize(page.nodes) : '';
}

export { DEFAULT_PAGE_ID, nextPageId };

const portalIdAttr = (node, annotate) =>
  annotate && node?.id ? ` data-portal-id="${node.id}"` : '';

const indentOf = depth => '  '.repeat(depth);

const serializeNode = (node, annotate = false, depth = 0) => {
  const indent = indentOf(depth);

  if (node.kind === 'html') {
    const content = node.content || '';
    if (!content) return '';
    if (!annotate || !node.id) {
      return content
        .split('\n')
        .map(line => `${indent}${line}`)
        .join('\n');
    }
    return `${indent}<div data-portal-id="${node.id}" style="display:contents">${content}</div>`;
  }

  if (node.kind === 'block') {
    const open = `<${node.tagName}${attrsToString(node.props)}${portalIdAttr(node, annotate)}>`;
    if (node.children?.length) {
      const inner = node.children
        .map(child => serializeNode(child, annotate, depth + 1))
        .filter(Boolean)
        .join('\n');
      return `${indent}${open}\n${inner}\n${indent}</${node.tagName}>`;
    }
    return `${indent}${open}${node.content || ''}</${node.tagName}>`;
  }

  if (node.kind === 'section') {
    const inner = (node.children || [])
      .map(child => serializeNode(child, annotate, depth + 1))
      .filter(Boolean)
      .join('\n');
    const attrs = `${attrsToString(node.props)}${portalIdAttr(node, annotate)}`;
    return inner
      ? `${indent}<portal-section${attrs}>\n${inner}\n${indent}</portal-section>`
      : `${indent}<portal-section${attrs}></portal-section>`;
  }

  if (node.kind === 'columns') {
    const cols = node.children || [];
    const spans = resolveColumnSpans(cols);
    const colIndent = indentOf(depth + 1);
    const colHtml = cols
      .map((col, i) => {
        const span = col.span ?? spans[i];
        const inner = (col.children || [])
          .map(child => serializeNode(child, annotate, depth + 2))
          .filter(Boolean)
          .join('\n');
        const idAttr = portalIdAttr(col, annotate);
        return inner
          ? `${colIndent}<column span="${span}"${idAttr}>\n${inner}\n${colIndent}</column>`
          : `${colIndent}<column span="${span}"${idAttr}></column>`;
      })
      .join('\n');
    const gap = node.gap ?? 6;
    const gapAttr = gap === 6 ? '' : ` gap="${gap}"`;
    return `${indent}<row${gapAttr}${attrsToString(node.props)}${portalIdAttr(node, annotate)}>\n${colHtml}\n${indent}</row>`;
  }

  if (node.kind === 'tabs') {
    const inner = (node.children || [])
      .map(child => serializeNode(child, annotate, depth + 1))
      .filter(Boolean)
      .join('\n');
    return `${indent}<portal-tabs${attrsToString(node.props)}${portalIdAttr(node, annotate)}>\n${inner}\n${indent}</portal-tabs>`;
  }

  if (node.kind === 'tab') {
    const inner = (node.children || [])
      .map(child => serializeNode(child, annotate, depth + 1))
      .filter(Boolean)
      .join('\n');
    const attrs = `${attrsToString(node.props)}${portalIdAttr(node, annotate)}`;
    return inner
      ? `${indent}<portal-tab${attrs}>\n${inner}\n${indent}</portal-tab>`
      : `${indent}<portal-tab${attrs}></portal-tab>`;
  }

  if (node.kind === 'wrapper') {
    const inner = (node.children || [])
      .map(child => serializeNode(child, annotate, depth + 1))
      .filter(Boolean)
      .join('\n');
    return `${indent}<${node.tagName}${attrsToString(node.props)}${portalIdAttr(node, annotate)}>\n${inner}\n${indent}</${node.tagName}>`;
  }

  if (node.kind === 'column') {
    // Should only appear inside columns serialize, but handle defensively
    const span = node.span ?? 12;
    const inner = (node.children || [])
      .map(child => serializeNode(child, annotate, depth + 1))
      .filter(Boolean)
      .join('\n');
    const idAttr = portalIdAttr(node, annotate);
    return inner
      ? `${indent}<column span="${span}"${idAttr}>\n${inner}\n${indent}</column>`
      : `${indent}<column span="${span}"${idAttr}></column>`;
  }

  return '';
};

export function serialize(nodes) {
  return (nodes || [])
    .map(node => serializeNode(node, false, 0))
    .filter(Boolean)
    .join('\n');
}

/** Same as serialize, but stamps session node ids for editor iframe overlay. Never persist. */
export function serializeForEditor(nodes) {
  return (nodes || [])
    .map(node => serializeNode(node, true, 0))
    .filter(Boolean)
    .join('\n');
}

export function resolveColumnSpans(columns) {
  const count = columns.length;
  if (!count) return [];
  const raw = columns.map(c => c.span);
  if (raw.every(s => s == null)) {
    const base = Math.floor(12 / count);
    const remainder = 12 % count;
    return columns.map((_, i) => base + (i === count - 1 ? remainder : 0));
  }
  return raw.map(s => (s == null ? Math.max(1, Math.floor(12 / count)) : s));
}

export function findNode(nodes, id, parent = null) {
  if (!nodes || !id) return null;
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (node.id === id) {
      return { node, parent, index: i, siblings: nodes };
    }
    if (node.children?.length) {
      const found = findNode(node.children, id, node);
      if (found) return found;
    }
  }
  return null;
}

export function findParentList(nodes, id) {
  const found = findNode(nodes, id);
  return found ? found.siblings : null;
}

export function isLayoutInsertTag(tagName) {
  return LAYOUT_INSERT_TAGS.has(tagName);
}

export function makeBlock(tagName, extras = {}) {
  if (tagName === 'layout-columns') {
    return makeColumnsRow(2);
  }
  if (tagName === 'portal-section') {
    return makeNode({
      kind: 'section',
      tagName: 'portal-section',
      children: [],
    });
  }
  if (tagName === 'portal-tabs') {
    return makeNode({
      kind: 'tabs',
      tagName: 'portal-tabs',
      children: [
        makeNode({
          kind: 'tab',
          tagName: 'portal-tab',
          props: { name: 'Details' },
          children: [],
        }),
        makeNode({
          kind: 'tab',
          tagName: 'portal-tab',
          props: { name: 'Documents' },
          children: [],
        }),
        makeNode({
          kind: 'tab',
          tagName: 'portal-tab',
          props: { name: 'Quote' },
          children: [],
        }),
      ],
    });
  }
  if (tagName === 'portal-tab') {
    return makeNode({
      kind: 'tab',
      tagName: 'portal-tab',
      props: { name: extras.name || 'Details' },
      children: [],
    });
  }
  return makeNode({
    kind: 'block',
    tagName,
    props: {},
    content: DEFAULT_CONTENT[tagName] || '',
  });
}

export function makeColumnsRow(count = 2) {
  const n = Math.min(6, Math.max(2, count));
  const span = Math.floor(12 / n);
  const remainder = 12 % n;
  return makeNode({
    kind: 'columns',
    gap: 6,
    children: Array.from({ length: n }, (_, i) =>
      makeNode({
        kind: 'column',
        span: span + (i === n - 1 ? remainder : 0),
        children: [],
      })
    ),
  });
}

/**
 * target: { parentId: string|null, index: number }
 * parentId null = root list
 */
export function insertNode(
  nodes,
  node,
  target = { parentId: null, index: null }
) {
  const list = resolveParentList(nodes, target.parentId);
  if (!list) return nodes;
  const index =
    target.index == null || target.index < 0 || target.index > list.length
      ? list.length
      : target.index;
  list.splice(index, 0, node);
  return nodes;
}

function resolveParentList(nodes, parentId) {
  if (parentId == null) return nodes;
  const found = findNode(nodes, parentId);
  if (!found) return null;
  const { node } = found;
  if (!node.children) node.children = [];
  return node.children;
}

export function removeNode(nodes, id) {
  const found = findNode(nodes, id);
  if (!found) return nodes;
  found.siblings.splice(found.index, 1);

  // Drop empty non-layout parent columns row if it becomes empty of columns
  if (found.parent?.kind === 'columns' && found.parent.children.length === 0) {
    removeNode(nodes, found.parent.id);
  }
  return nodes;
}

export function moveNode(nodes, id, target) {
  const found = findNode(nodes, id);
  if (!found) return nodes;

  // Prevent moving into self or descendant
  if (target.parentId === id) return nodes;
  if (target.parentId) {
    const dest = findNode(nodes, target.parentId);
    if (dest && isDescendant(found.node, target.parentId)) return nodes;
  }

  const [removed] = found.siblings.splice(found.index, 1);

  // Adjust index if moving within same list and removing before insert point
  let { index } = target;
  const destList = resolveParentList(nodes, target.parentId);
  if (!destList) {
    // restore
    found.siblings.splice(found.index, 0, removed);
    return nodes;
  }
  if (destList === found.siblings && index != null && found.index < index) {
    index -= 1;
  }
  const insertAt =
    index == null || index < 0 || index > destList.length
      ? destList.length
      : index;
  destList.splice(insertAt, 0, removed);
  return nodes;
}

function isDescendant(node, id) {
  if (!node.children?.length) return false;
  return node.children.some(
    child => child.id === id || isDescendant(child, id)
  );
}

export function applyColumnCount(node, count) {
  if (!node || node.kind !== 'columns') return;
  const nextCount = Math.min(6, Math.max(2, Number(count) || 2));
  const current = node.children || [];

  if (nextCount === current.length) return;

  if (nextCount > current.length) {
    const toAdd = nextCount - current.length;
    for (let i = 0; i < toAdd; i += 1) {
      current.push(makeNode({ kind: 'column', span: 1, children: [] }));
    }
  } else {
    const kept = current.slice(0, nextCount);
    const removed = current.slice(nextCount);
    removed.forEach(col => {
      kept[kept.length - 1].children.push(...(col.children || []));
    });
    node.children = kept;
  }

  const span = Math.floor(12 / nextCount);
  const remainder = 12 % nextCount;
  node.children.forEach((col, i) => {
    col.span = span + (i === nextCount - 1 ? remainder : 0);
  });
}

export function applyColumnGap(node, gap) {
  if (!node || node.kind !== 'columns') return;
  node.gap = Math.min(12, Math.max(0, Number(gap) || 0));
}

export function updateNodeProps(node, props) {
  if (!node) return;
  node.props = { ...node.props, ...props };
}

export function updateNodeContent(node, content) {
  if (!node) return;
  node.content = content;
}

/**
 * Resolve where to insert when clicking "add" from the palette.
 * Into selected container, else after selected node, else append to root.
 */
export function resolveInsertTarget(nodes, selectedId, tagName) {
  const isLayout = isLayoutInsertTag(tagName);

  if (!selectedId) {
    return { parentId: null, index: nodes.length };
  }

  const found = findNode(nodes, selectedId);
  if (!found) {
    return { parentId: null, index: nodes.length };
  }

  const { node, parent, index } = found;
  const containerKinds = new Set([
    'section',
    'column',
    'tab',
    'wrapper',
    'tabs',
  ]);

  // Layout inserts always as siblings of the selection (or into section/wrapper)
  if (isLayout) {
    if (
      node.kind === 'section' ||
      node.kind === 'wrapper' ||
      node.kind === 'column'
    ) {
      return { parentId: node.id, index: (node.children || []).length };
    }
    return {
      parentId: parent?.id ?? null,
      index: index + 1,
    };
  }

  // Leaf inserts go into containers
  if (containerKinds.has(node.kind) && node.kind !== 'tabs') {
    return { parentId: node.id, index: (node.children || []).length };
  }
  if (node.kind === 'tabs') {
    // Insert into first tab if present
    const firstTab = (node.children || []).find(c => c.kind === 'tab');
    if (firstTab) {
      return {
        parentId: firstTab.id,
        index: (firstTab.children || []).length,
      };
    }
  }
  if (node.kind === 'columns') {
    const firstCol = node.children?.[0];
    if (firstCol) {
      return {
        parentId: firstCol.id,
        index: (firstCol.children || []).length,
      };
    }
  }

  // After selected sibling
  return {
    parentId: parent?.id ?? null,
    index: index + 1,
  };
}

export function cloneTree(nodes) {
  return JSON.parse(JSON.stringify(nodes));
}

const cloneNodeWithNewIds = node => {
  const copy = {
    ...node,
    id: nextId(),
    props: { ...(node.props || {}) },
  };
  if (node.children?.length) {
    copy.children = node.children.map(cloneNodeWithNewIds);
  } else {
    copy.children = [];
  }
  return copy;
};

/** Insert a deep copy of the node immediately after it. Returns the new node. */
export function duplicateNode(nodes, id) {
  const found = findNode(nodes, id);
  if (!found) return null;
  const copy = cloneNodeWithNewIds(found.node);
  found.siblings.splice(found.index + 1, 0, copy);
  return copy;
}

export { DEFAULT_CONTENT, LAYOUT_INSERT_TAGS };
